import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import { EXCEPTION_MAX_WEEKS_AHEAD, NOTE_MAX_LENGTH } from '../constants';
import { apiErrorMessage } from '../hooks';
import { useUpsertExceptionMutation } from '../query';
import { AvailabilityDay, AvailabilityException, Weekday } from '../types';
import {
  exceptionByWeek,
  ExceptionDayDraft,
  ExceptionDayMode,
  isoWeekRangeLabel,
  toExceptionDays,
  toExceptionDrafts,
  upcomingIsoWeeks,
  validateWeekRanges,
  weekdayLabel
} from '../utils';
import { EditorDialog } from './EditorDialog';
import { RangeListEditor } from './RangeListEditor';

interface Props {
  open: boolean;
  onClose: () => void;
  exceptions: AvailabilityException[];
  templateDays: AvailabilityDay[];
  /** Preselects a week when editing an existing exception. */
  initialWeek?: { year: number; calendarWeek: number };
  onSaved: () => void;
}

const weekKey = (year: number, calendarWeek: number) =>
  `${year}-${calendarWeek}`;

/** Upserts the availability exception of one ISO week. */
export const ExceptionEditorDialog = ({
  open,
  onClose,
  exceptions,
  templateDays,
  initialWeek,
  onSaved
}: Props) => {
  const weeks = upcomingIsoWeeks(EXCEPTION_MAX_WEEKS_AHEAD);
  const [selectedKey, setSelectedKey] = useState(
    weekKey(weeks[0].year, weeks[0].calendarWeek)
  );
  const [wholeWeekUnavailable, setWholeWeekUnavailable] = useState(false);
  const [note, setNote] = useState('');
  const [drafts, setDrafts] = useState<ExceptionDayDraft[]>(
    toExceptionDrafts(null)
  );
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpsertExceptionMutation();

  const selectedWeek =
    weeks.find(week => weekKey(week.year, week.calendarWeek) === selectedKey) ??
    weeks[0];

  const loadWeek = (year: number, calendarWeek: number) => {
    const existing = exceptionByWeek(exceptions, year, calendarWeek);
    setWholeWeekUnavailable(existing?.wholeWeekUnavailable ?? false);
    setNote(existing?.note ?? '');
    setDrafts(toExceptionDrafts(existing?.days));
    setError(null);
  };

  useEffect(() => {
    if (open) {
      const week = initialWeek ?? weeks[0];
      setSelectedKey(weekKey(week.year, week.calendarWeek));
      loadWeek(week.year, week.calendarWeek);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialWeek?.year, initialWeek?.calendarWeek]);

  const handleWeekChange = (key: string) => {
    setSelectedKey(key);
    const [year, calendarWeek] = key.split('-').map(Number);
    loadWeek(year, calendarWeek);
  };

  const updateDraft = (dayOfWeek: Weekday, patch: Partial<ExceptionDayDraft>) =>
    setDrafts(
      drafts.map(draft =>
        draft.dayOfWeek === dayOfWeek ? { ...draft, ...patch } : draft
      )
    );

  const handleModeChange = (dayOfWeek: Weekday, mode: ExceptionDayMode) => {
    if (mode === 'CUSTOM') {
      // Prefill a fresh custom day from the weekly template so edits start
      // from what would apply anyway.
      const draft = drafts.find(d => d.dayOfWeek === dayOfWeek);
      const template = templateDays.find(d => d.dayOfWeek === dayOfWeek);
      updateDraft(dayOfWeek, {
        mode,
        available: draft?.available.length
          ? draft.available
          : template?.available ?? [],
        preferred: draft?.preferred.length
          ? draft.preferred
          : template?.preferred ?? []
      });
      return;
    }
    updateDraft(dayOfWeek, { mode });
  };

  const handleSave = () => {
    const days = toExceptionDays(drafts);

    if (!wholeWeekUnavailable) {
      if (!days.length) {
        setError(getTrans('availabilityPage.validation.exception.no-days'));
        return;
      }
      const customDays: AvailabilityDay[] = drafts
        .filter(draft => draft.mode === 'CUSTOM')
        .map(draft => ({
          dayOfWeek: draft.dayOfWeek,
          available: draft.available,
          preferred: draft.preferred
        }));
      const emptyCustomDay = customDays.some(day => !day.available.length);
      if (emptyCustomDay) {
        setError(getTrans('availabilityPage.validation.exception.day-empty'));
        return;
      }
      const validationError = validateWeekRanges(customDays);
      if (validationError) {
        setError(
          getTrans(
            `availabilityPage.validation.${validationError}` as TranslationKey
          )
        );
        return;
      }
    }

    mutation.mutate(
      {
        year: selectedWeek.year,
        calendarWeek: selectedWeek.calendarWeek,
        body: {
          wholeWeekUnavailable,
          note: note.trim() || null,
          days: wholeWeekUnavailable ? [] : days
        }
      },
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
        onError: responseError => setError(apiErrorMessage(responseError))
      }
    );
  };

  return (
    <EditorDialog
      open={open}
      title={getTrans('availabilityPage.exceptionEditor.title')}
      onClose={onClose}
      onSave={handleSave}
      saving={mutation.isLoading}
      dataTestId="exception-editor"
    >
      <TextField
        select
        fullWidth
        label={getTrans('availabilityPage.exceptionEditor.weekLabel')}
        value={selectedKey}
        onChange={event => handleWeekChange(event.target.value)}
        sx={{ mb: 2 }}
        data-testid="exception-week-select"
      >
        {weeks.map(week => (
          <MenuItem
            key={weekKey(week.year, week.calendarWeek)}
            value={weekKey(week.year, week.calendarWeek)}
          >
            {getTransWithParams('availabilityPage.exceptionEditor.weekOption', {
              week: week.calendarWeek,
              range: isoWeekRangeLabel(week)
            })}
            {exceptionByWeek(exceptions, week.year, week.calendarWeek)
              ? ` · ${getTrans(
                  'availabilityPage.exceptionEditor.hasException'
                )}`
              : ''}
          </MenuItem>
        ))}
      </TextField>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-testid="exception-editor-error"
        >
          {error}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={wholeWeekUnavailable}
            onChange={event => setWholeWeekUnavailable(event.target.checked)}
            inputProps={
              {
                'data-testid': 'whole-week-switch'
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
        }
        label={getTrans('availabilityPage.exceptionEditor.wholeWeek')}
        sx={{ mb: 1 }}
      />

      {!wholeWeekUnavailable && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {getTrans('availabilityPage.exceptionEditor.hint')}
          </Typography>
          {drafts.map(draft => (
            <Box key={draft.dayOfWeek} sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>
                  {weekdayLabel(draft.dayOfWeek)}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={draft.mode}
                  onChange={(_, mode: ExceptionDayMode | null) =>
                    mode && handleModeChange(draft.dayOfWeek, mode)
                  }
                >
                  <ToggleButton value="AS_USUAL">
                    {getTrans('availabilityPage.exceptionEditor.asUsual')}
                  </ToggleButton>
                  <ToggleButton value="UNAVAILABLE">
                    {getTrans('availabilityPage.day.unavailable')}
                  </ToggleButton>
                  <ToggleButton value="CUSTOM">
                    {getTrans('availabilityPage.exceptionEditor.custom')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {draft.mode === 'CUSTOM' && (
                <Box sx={{ pl: 1 }}>
                  <RangeListEditor
                    label={getTrans('availabilityPage.editor.availableLabel')}
                    ranges={draft.available}
                    onChange={available =>
                      updateDraft(draft.dayOfWeek, { available })
                    }
                    addLabel={getTrans('availabilityPage.editor.addHours')}
                    dataTestId={`exception-${draft.dayOfWeek}-available`}
                  />
                  <RangeListEditor
                    label={getTrans('availabilityPage.editor.preferredLabel')}
                    ranges={draft.preferred}
                    onChange={preferred =>
                      updateDraft(draft.dayOfWeek, { preferred })
                    }
                    addLabel={getTrans('availabilityPage.editor.addPreferred')}
                    dataTestId={`exception-${draft.dayOfWeek}-preferred`}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      <TextField
        fullWidth
        label={getTrans('availabilityPage.exceptionEditor.noteLabel')}
        placeholder={getTrans(
          'availabilityPage.exceptionEditor.notePlaceholder'
        )}
        value={note}
        onChange={event => setNote(event.target.value)}
        inputProps={{
          maxLength: NOTE_MAX_LENGTH,
          'data-testid': 'exception-note'
        }}
      />
    </EditorDialog>
  );
};
